import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { HStack, Link, rem, VStack } from '@bonusly/design-system';
import { useCurrentUser } from '@bonusly/who-am-i';

import { Panel } from '../../../../components/panel';
import { NotificationEmptyState, NotificationItem } from '../../../top-navbar/components/Notification';
import { useFetchNotifications } from '../../../top-navbar/components/Notification/hooks/useFetchNotifications';
import { useInvalidateNotifications } from '../../../top-navbar/components/Notification/hooks/useInvalidateNotifications';
import { NotificationUpdateType, useUpdateNotifications } from '../../../top-navbar/components/Notification/hooks/useUpdateNotifications';
import { NotificationStatus } from '../../../top-navbar/lib/notification/notification';

interface NotificationPanelProps {
	isOpen: boolean;
	isSidebarExpanded: boolean;
	onClose: () => void;
}

export const NotificationPanel: FC<NotificationPanelProps> = ({ isOpen, isSidebarExpanded, onClose }) => {
	const { t } = useTranslation('navigation');
	const user = useCurrentUser();
	const invalidate = useInvalidateNotifications();
	const { notifications, unreadCount } = useFetchNotifications(user.locale);
	const { track } = useAnalytics();
	const handleUpdateNotification = useUpdateNotifications('mark-all-read', {});
	// Track if we've already marked notifications as seen for the current open session
	const hasMarkedAsSeenRef = useRef(false);

	// Reset the "marked as seen" flag when panel closes
	useEffect(() => {
		if (!isOpen) {
			hasMarkedAsSeenRef.current = false;
		}
	}, [isOpen]);

	// Track panel view when panel opens
	useEffect(() => {
		if (!isOpen) return;
		track(AnalyticsEvent.TopNavbar.NotificationsViewList);
	}, [isOpen, track]);

	// Mark notifications as seen when panel is open and notifications are loaded
	useEffect(() => {
		if (!isOpen) return;
		if (hasMarkedAsSeenRef.current) return;
		if (unreadCount === 0) return;

		const unReadNotifications = notifications.filter((notification) => notification.status === NotificationStatus.Unread);

		if (unReadNotifications.length === 0) return;

		hasMarkedAsSeenRef.current = true;

		const markAsSeen = async () => {
			const updateNotifications = unReadNotifications.map((notification) =>
				handleUpdateNotification.mutateAsync({
					notificationId: notification.id,
					type: NotificationUpdateType.MarkAsSeen,
				})
			);

			await Promise.all(updateNotifications);
			invalidate();
		};

		markAsSeen();
	}, [isOpen, notifications, unreadCount, handleUpdateNotification, invalidate]);

	const handleMarkAllRead = async () => {
		const unReadNotifications =
			notifications
				?.filter((notification) => notification.status !== NotificationStatus.Read)
				.map((notification) =>
					handleUpdateNotification.mutateAsync({
						notificationId: notification.id,
						type: NotificationUpdateType.MarkAsRead,
					})
				) ?? [];

		await Promise.all(unReadNotifications);
		invalidate();
		track(AnalyticsEvent.TopNavbar.NotificationsMarkAllRead);
	};

	return (
		<Panel
			isOpen={isOpen}
			onClose={onClose}
			isSidebarExpanded={isSidebarExpanded}
			title={t('notification.header')}
			closeButtonLabel={t('notification.close')}
			width={rem(350)}
			data-testid="notification-panel"
		>
			{/* Mark all as read link - only show if there are notifications not yet read */}
			{notifications.some((notification) => notification.status !== NotificationStatus.Read) && (
				<HStack justify="flex-end" px={rem(8)}>
					<Link
						onClick={handleMarkAllRead}
						textColor="primary.fgStrong"
						as="a"
						tabIndex={0}
						cursor="pointer"
						fontSize="sm"
						mb={1}
						_focusVisible={{ outline: '2px solid', outlineColor: 'blue.500', borderRadius: '1px', outlineOffset: '-2px' }}
					>
						{t('notification.read_all')}
					</Link>
				</HStack>
			)}

			{/* Notification list */}
			<VStack as="nav" aria-label={t('notification.menu_label')} gap={0} w="100%">
				{notifications.length > 0 ? (
					notifications.map((notification) => <NotificationItem notification={notification} key={notification.id} handleClose={onClose} />)
				) : (
					<NotificationEmptyState />
				)}
			</VStack>
		</Panel>
	);
};
